import React, { useMemo } from 'react';
import styles from './ORSMOnboarding.module.scss';
import Text from '../../../../Components/Base/Text/Text';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import ORSMFaqs from '../FAQS/ORSMFaqs';
import ORSMActivationModal from '../Activation/ORSMActivationModal';
import { useDisclose } from '../../../Modal/Hooks/useDisclose';
import FeatureCard from '../../../../Components/Base/FeatureCard/FeatureCard';
import useTranslation from '../../../../Hooks/useTranslation';
import {
	Subscription,
	SubscriptionPeriod,
	SubscriptionPeriodType,
	useWorkplaceAvailableSubscriptionQuery,
} from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';

interface Props {
	rootWorkplaceId: string;
}

const ORSMOnboarding: React.FC<Props> = ({ rootWorkplaceId }) => {
	const { isOpen, onOpen, onClose } = useDisclose(false);
	const { t } = useTranslation();
	const { data, isLoading } = useWorkplaceAvailableSubscriptionQuery({
		where: { code: 'osmr_workplace_subscription' },
	});

	const subscription: Subscription | undefined = useMemo(() => {
		if (!data) return undefined;
		return data?.workplaceAvailableSubscription as Subscription;
	}, [data]);

	const subscriptionPeriod: SubscriptionPeriod | undefined = useMemo(() => {
		if (!subscription) return undefined;
		return subscription.subscriptionPeriods?.find(
			item => item.type === SubscriptionPeriodType.Year,
		);
	}, [subscription]);

	if (isLoading) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.hero}>
				<div className={styles.content}>
					<div className={styles.badge}>
						<img
							src='/dist/icons/subscription/star-regular-full.svg'
							alt='star'
							height={15}
						/>{' '}
						{t('component.ORSMOnboarding.new')}
					</div>
					<div className={styles.title}>
						{t('component.ORSMOnboarding.revolution')}:
						<span>
							{' '}
							{t('component.ORSMOnboarding.onlineReservationSystem')}
						</span>
					</div>
					<Text tx='component.ORSMOnboarding.text1' />
					<Text tx='component.ORSMOnboarding.text2' noEscape />
					{subscriptionPeriod?.price?.formattedPrice && (
						<Button
							title='component.ORSMOnboarding.activateFor'
							txParams={{ price: subscriptionPeriod.price.formattedPrice }}
							theme={Theme.BLUE}
							fitContent
							onClick={onOpen}
							className={styles.button}
						/>
					)}
				</div>
				<div className={styles.mockWrapper}></div>
			</div>
			<div className={styles.cardGrid}>
				<FeatureCard
					icon='/dist/icons/subscription/clock-regular-full.svg'
					title='component.ORSMOnboarding.card1.title'
					text='component.ORSMOnboarding.card1.text'
				/>
				<FeatureCard
					icon='/dist/icons/subscription/message-dots-regular-full.svg'
					title='component.ORSMOnboarding.card2.title'
					text='component.ORSMOnboarding.card2.text'
				/>
				<FeatureCard
					icon='/dist/icons/subscription/arrows-rotate-sharp-duotone-solid-full.svg'
					title='component.ORSMOnboarding.card3.title'
					text='component.ORSMOnboarding.card3.text'
				/>
				<FeatureCard
					icon='/dist/icons/subscription/money-bills-regular-full.svg'
					title='component.ORSMOnboarding.card4.title'
					text='component.ORSMOnboarding.card4.text'
				/>
			</div>
			<div></div>
			<div className={styles.howItWorks}>
				<div>
					<Text
						tx='component.ORSMOnboarding.howItWorks'
						className={styles.title}
					/>
					<Text
						tx='component.ORSMOnboarding.notMoreThan5Minutes'
						className={styles.subtitle}
					/>
				</div>
				<div className={styles.steps}>
					<div className={styles.step}>
						<div className={styles.number}>1</div>
						<Text
							tx='component.ORSMOnboarding.step1.title'
							className={styles.stepTitle}
						/>
						<Text
							tx='component.ORSMOnboarding.step1.text'
							className={styles.steptext}
						/>
					</div>
					<div className={styles.line} />
					<div className={styles.step}>
						<div className={styles.number}>2</div>
						<Text
							tx='component.ORSMOnboarding.step2.title'
							className={styles.stepTitle}
						/>
						<Text
							tx='component.ORSMOnboarding.step2.text'
							className={styles.steptext}
						/>
					</div>
					<div className={styles.line} />
					<div className={styles.step}>
						<div className={styles.number}>3</div>
						<Text
							tx='component.ORSMOnboarding.step3.title'
							className={styles.stepTitle}
						/>
						<Text
							tx='component.ORSMOnboarding.step3.text'
							className={styles.steptext}
						/>
					</div>
				</div>
			</div>
			<div></div>
			<ORSMFaqs />
			{isOpen && subscription && subscriptionPeriod && (
				<ORSMActivationModal
					isOpen
					onClose={onClose}
					rootWorkplaceId={rootWorkplaceId}
					subscription={subscription}
					subscriptionPeriod={subscriptionPeriod}
				/>
			)}
		</div>
	);
};

export default ORSMOnboarding;
